import { ORDER_STATUS_COLORS } from '../constants';

/**
 * The order number shown everywhere (Order Management, PDFs, Credit Control) —
 * dogma_ordernumber is the source of truth, falling back to the legacy
 * ordernumber field for older records that predate it.
 */
export const getOrderNumber = (order?: { dogma_ordernumber?: string | null; ordernumber?: string | null } | null): string =>
  order?.dogma_ordernumber || order?.ordernumber || '';

/**
 * Resolve a Tag colour for an order status. Some real Dataverse statuscodes
 * (e.g. whatever renders as "Pending Credit Control") aren't in our static
 * ORDER_STATUS_COLORS map, which left them stuck on grey — this falls back
 * to keyword-matching the resolved label text so statuses like "Cancelled"
 * or "...Credit Control" still get a meaningful colour instead of grey.
 */
export const getOrderStatusColor = (statuscode: number | undefined, label: string | undefined): string => {
  if (statuscode !== undefined && ORDER_STATUS_COLORS[statuscode]) {
    return ORDER_STATUS_COLORS[statuscode];
  }
  const text = (label || '').toLowerCase();
  if (text.includes('cancel')) return 'error';
  if (text.includes('credit') || text.includes('pending') || text.includes('hold')) return 'warning';
  if (text.includes('complete') || text.includes('fulfil')) return 'success';
  if (text.includes('receive')) return 'cyan';
  if (text.includes('production')) return 'processing';
  if (text.includes('payment')) return 'purple';
  return 'default';
};

/**
 * Generate status filter query for orders
 */
export const generateStatusFilterQuery = (statuscode: number): string => {
  return `statuscode eq ${statuscode}`;
};

/**
 * Generate account filter query for orders (via quote relationship)
 */
export const generateAccountFilterQuery = (accountId: string): string => {
  if (!accountId) return "";
  return `quoteid/_dogma_customer_value eq '${accountId}'`;
};

/**
 * Generate payment status filter query for orders
 */
export const generatePaymentStatusFilterQuery = (paymentStatus: number): string => {
  return `dogma_paymentstatus eq ${paymentStatus}`;
};

/**
 * Generate date range filter query for orders
 */
export const generateDateRangeFilterQuery = (startDate: string, endDate: string): string => {
  if (!startDate && !endDate) return "";
  const parts: string[] = [];
  if (startDate) parts.push(`createdon ge ${startDate}`);
  if (endDate) parts.push(`createdon le ${endDate}`);
  return parts.join(" and ");
};

/**
 * Generate project type filter query for orders (via quote relationship)
 */
export const generateProjectTypeFilterQuery = (projectType: number): string => {
  return `quoteid/dogma_projecttype eq ${projectType}`;
};

/**
 * Combine multiple filter queries with 'and' operator
 */
export const combineFilterQueries = (filters: string[]): string => {
  const validFilters = filters.filter(f => f && f.trim() !== '');
  return validFilters.length > 0 ? validFilters.join(' and ') : '';
};
